import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Loading,
  Message,
  Modal,
  Tabs,
  Text,
  toast,
} from '@workwhile/ui'
import { useCompanyDetailWorkflowsContextValue } from '../../../context/CompanyDetailWorkflowProvider'
import { useEditCompanyFileSchemaFlowContextValue } from '../context/EditCompanyFileSchemaFlowProvider'

const emptySchema = '{\n  "fields": []\n}'

const PDF_MAPPED_FORM_COMPONENTS = new Set([
  'Checkbox',
  'DatePicker',
  'Image',
  'Options',
  'Select',
  'Text',
])

type FormSchemaField = {
  name?: string | number | null
  component?: string
  fields?: FormSchemaField[]
}

function collectPdfMappedFieldNames(fields: FormSchemaField[]): string[] {
  const names: string[] = []
  for (const field of fields) {
    if (field.fields) {
      names.push(...collectPdfMappedFieldNames(field.fields))
    }
    if (
      typeof field.component === 'string' &&
      PDF_MAPPED_FORM_COMPONENTS.has(field.component) &&
      typeof field.name === 'string'
    ) {
      names.push(field.name)
    }
  }
  return names
}

function validateSchemaAgainstPdfFields(
  schema: Record<string, unknown>,
  pdfFieldKeys: string[]
) {
  const fields = Array.isArray(schema.fields)
    ? (schema.fields as FormSchemaField[])
    : []
  const schemaFieldNames = new Set(collectPdfMappedFieldNames(fields))
  if (schemaFieldNames.size === 0) {
    return
  }

  const pdfKeys = new Set(pdfFieldKeys)
  const missing = pdfFieldKeys
    .filter((key) => !schemaFieldNames.has(key))
    .sort()
  const unexpected = Array.from(schemaFieldNames)
    .filter((key) => !pdfKeys.has(key))
    .sort()
  if (missing.length === 0 && unexpected.length === 0) {
    return
  }
}

function titleCaseFieldName(name: string) {
  return name
    .split(/[_\s.-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export const EditCompanyFileSchemaFlowMain = () => {
  const {
    editCompanyFileSchemaFlow: { isOpen, close, file },
  } = useCompanyDetailWorkflowsContextValue()
  const {
    state: {
      fields,
      pdfFieldKeys,
      isLoadingFields,
      fieldsErrorMessage,
      annotateMutationState,
      previewMutationState,
    },
    actions: { annotate, resetAnnotate, preview, resetPreview },
  } = useEditCompanyFileSchemaFlowContextValue()

  const [schemaText, setSchemaText] = useState(emptySchema)
  const [fillValues, setFillValues] = useState<Record<string, string>>({})
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!file || !isOpen) return
    setSchemaText(
      file.extra ? JSON.stringify(file.extra, null, 2) : emptySchema
    )
    setFillValues({})
    resetAnnotate()
    resetPreview()
    setPreviewUrl(null)
  }, [file, isOpen, resetAnnotate, resetPreview])

  useEffect(() => {
    if (!previewMutationState.data) return
    const url = URL.createObjectURL(previewMutationState.data)
    setPreviewUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [previewMutationState.data])

  useEffect(() => {
    setFillValues((previous) => {
      const next: Record<string, string> = {}
      for (const key of pdfFieldKeys) {
        next[key] = previous[key] ?? ''
      }
      return next
    })
  }, [pdfFieldKeys])

  const fieldsByPage = useMemo(() => {
    const grouped = new Map<number, typeof fields>()
    for (const field of fields) {
      const pageFields = grouped.get(field.page) ?? []
      pageFields.push(field)
      grouped.set(field.page, pageFields)
    }
    return Array.from(grouped.entries()).sort(([a], [b]) => a - b)
  }, [fields])

  if (!file) return null

  const handleClose = () => {
    resetAnnotate()
    resetPreview()
    setPreviewUrl(null)
    close()
  }

  const handleSaveSchema = () => {
    let parsed: Record<string, unknown>
    try {
      parsed = JSON.parse(schemaText) as Record<string, unknown>
    } catch {
      toast.error('FormSchema must be valid JSON')
      return
    }

    try {
      validateSchemaAgainstPdfFields(parsed, pdfFieldKeys)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'FormSchema fields do not match this PDF'
      )
      return
    }

    annotate(
      { fileId: file.id, schema: parsed },
      {
        onSuccess: () => {
          toast.success('Saved FormSchema')
        },
      }
    )
  }

  const handlePreview = () => {
    const data: Record<string, string | boolean> = {}
    for (const [key, value] of Object.entries(fillValues)) {
      data[key] = value
    }
    preview(
      { fileId: file.id, data },
      {
        onError: () => {
          toast.error('Failed to generate preview PDF')
        },
      }
    )
  }

  const isBusy =
    annotateMutationState.isPending || previewMutationState.isPending

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isBusy}
      showCancel
      // @ts-expect-error - width is not a valid prop for Modal
      width="60rem"
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Close
          </Button>
        </Flex>
      }
    >
      <Box>
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Edit File Schema
        </Heading>
        <Text mt={2} fontSize={1} color="lightText">
          {file.path}
        </Text>
        <Text mt={1} fontSize={1}>
          <a href={file.tempUrl} target="_blank" rel="noreferrer">
            Open original PDF
          </a>
        </Text>

        <Tabs
          defaultValue="schema"
          // @ts-expect-error - mt is not a valid prop for Tabs
          mt={3}
          tabs={[
            { value: 'schema', label: 'Schema' },
            { value: 'test_fill', label: 'Test fill' },
          ]}
        >
          <Tabs.Content value="schema">
            <Field
              label="FormSchema JSON" // @ts-expect-error - mt is not a valid prop for Field
              mt={3}
            >
              <textarea
                value={schemaText}
                onChange={(event) => setSchemaText(event.target.value)}
                rows={18}
                style={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  width: '100%',
                  resize: 'vertical',
                }}
              />
            </Field>
            {annotateMutationState.errorMessage ? (
              <Message variant="error" mt={3} borderRadius="small">
                {annotateMutationState.errorMessage}
              </Message>
            ) : null}
            <Flex mt={3} justifyContent="flex-end">
              <Button variant="primary" onClick={handleSaveSchema}>
                Save schema
              </Button>
            </Flex>
          </Tabs.Content>

          <Tabs.Content value="test_fill">
            <Text mt={3} fontSize={1} color="lightText">
              Add test data and preview a filled PDF without creating a worker
              signature.
            </Text>
            <Message variant="warning" mt={3} borderRadius="small">
              Preview inputs may differ from the worker app. After saving, add
              this tutorial to a test RSW shift/position on company 3 and verify
              in the app.
            </Message>
            {isLoadingFields ? (
              <Box mt={3}>
                <Loading />
              </Box>
            ) : null}
            {fieldsErrorMessage ? (
              <Message variant="error" mt={3} borderRadius="small">
                {fieldsErrorMessage}
              </Message>
            ) : null}
            {!isLoadingFields && pdfFieldKeys.length === 0 ? (
              <Message variant="warning" mt={3} borderRadius="small">
                This PDF is not fillable. Please upload a fillable PDF with
                field annotations included.
              </Message>
            ) : null}
            <Box mt={3}>
              {fieldsByPage.map(([page, pageFields]) => (
                <Box key={page} mb={3}>
                  <Text fontSize={1} fontWeight="bold" mb={2}>
                    Page {page + 1}
                  </Text>
                  {pageFields.map((field) => {
                    const key = `${field.page}.${field.name}`
                    return (
                      <Field
                        key={key}
                        label={titleCaseFieldName(field.name)}
                        // @ts-expect-error - mb is not a valid prop for Field
                        mb={2}
                      >
                        <Input
                          value={fillValues[key] ?? ''}
                          onChange={(event) =>
                            setFillValues((previous) => ({
                              ...previous,
                              [key]: event.target.value,
                            }))
                          }
                        />
                      </Field>
                    )
                  })}
                </Box>
              ))}
            </Box>
            {previewMutationState.errorMessage ? (
              <Message variant="error" mt={3} borderRadius="small">
                {previewMutationState.errorMessage}
              </Message>
            ) : null}
            <Flex mt={3} justifyContent="flex-end" gap={2}>
              <Button
                variant="primary"
                onClick={handlePreview}
                disabled={isLoadingFields || pdfFieldKeys.length === 0}
              >
                Generate preview
              </Button>
            </Flex>
            {previewUrl ? (
              <Box mt={3}>
                <iframe
                  title="Filled PDF preview"
                  src={previewUrl}
                  width="100%"
                  height={480}
                  style={{ border: '1px solid #ddd', borderRadius: 4 }}
                />
                <Text mt={2} fontSize={1}>
                  <a href={previewUrl} download={`preview-${file.id}.pdf`}>
                    Download filled PDF
                  </a>
                </Text>
              </Box>
            ) : null}
          </Tabs.Content>
        </Tabs>
      </Box>
    </Modal>
  )
}
